import { fontColorDarkTheme } from '../../styles/global.styles';
import * as React from 'react';
import { ReactElement, ReactNode, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import {
  Box,
  Checkbox,
  Skeleton,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { OverridableStringUnion } from '@mui/types';
import { TablePropsSizeOverrides } from '@mui/material/Table/Table';
import { IPaginationParams } from '../../services/types';
import MainFilter from './_components/MainFilter';
import TableBodyContent from './_components/TableBodyContent';
import { t } from 'i18next';

export interface IColumnProps {
  id?: string;
  label: string;
  width?: number | string;
  height?: number | string;
  tooltipMessage?: string;
  minWidth?: number;
  numeric?: boolean;
  disablePadding?: boolean;
  align?: 'right' | 'center';
  format?: (value: number) => string | React.ReactElement;
  render?: (value: string, rowIndex: number, row?: any) => React.ReactElement;
}

interface TableComponentProps {
  rows: readonly any[];
  title?: string | ReactElement;
  size?: OverridableStringUnion<'small' | 'medium', TablePropsSizeOverrides>;
  sortByName?: string;
  isLoading?: boolean;
  defaultOrder?: Order;
  isPaginated?: boolean;
  onChangeMainFilter?: (value: string) => void;
  onSubmitMainFilter?: () => void;
  onOpenFilter?: () => void;
  searchValue?: string;
  totalCount?: number;
  keyTable?: number;
  onChangePageOrRowsPerPage?: (paginationParams: IPaginationParams) => void;
  additionalTitleComponent?: ReactNode;
  columns: IColumnProps[];
  margin?: string | number;
  emptyMessage?: string;
  height?: string | number;
  isExpandedRow?: boolean;
  expandedRowContent?: (row: any, index: any) => ReactElement;
  width?: string | number;
  onRowClick?: (item: any, index: number) => void;
  isEnableSelection?: boolean;
  fieldToBlockedToSelect?: string;
  isEnableSort?: boolean;
  currentPageNumber?: number;
}

type Order = 'asc' | 'desc';

interface EnhancedTableProps {
  numSelected: number;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof any,
  ) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: string | number | symbol;
  rowCount: number;
  isEnableSelection?: boolean;
}

const TableComponent = ({
  rows,
  columns,
  searchValue,
  sortByName,
  title,
  size = 'small',
  defaultOrder = 'desc',
  isLoading,
  expandedRowContent,
  isExpandedRow,
  emptyMessage,
  isPaginated = true,
  onChangeMainFilter,
  onOpenFilter,
  onSubmitMainFilter,
  totalCount,
  additionalTitleComponent,
  margin,
  keyTable,
  height,
  width,
  onRowClick,
  isEnableSort = false,
  onChangePageOrRowsPerPage,
  currentPageNumber,
  isEnableSelection,
  fieldToBlockedToSelect,
}: TableComponentProps) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState<Order>(defaultOrder);
  const [orderBy, setOrderBy] = useState<keyof any>(sortByName ?? 'name');
  const [selected, setSelected] = React.useState<readonly number[]>([]);

  React.useEffect(() => {
    // Verifica se a página atual está fora dos limites do novo total de itens
    if (totalCount !== undefined && totalCount > 0 && (page * rowsPerPage) >= totalCount) {
      setPage(0);
      
      if (onChangePageOrRowsPerPage) {
        onChangePageOrRowsPerPage({
          pageNumber: 1,
          pageSize: rowsPerPage,
        });
      }
    }
  }, [totalCount, rowsPerPage]);

  React.useEffect(() => {
    if (currentPageNumber !== undefined && currentPageNumber > 0) {
      const convertedPageForMUI = currentPageNumber - 1;
      if (page !== convertedPageForMUI) {
         setPage(convertedPageForMUI);
      }
    }
  }, [currentPageNumber]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    if (onChangePageOrRowsPerPage)
      onChangePageOrRowsPerPage({
        pageNumber: newPage + 1,
        pageSize: rowsPerPage,
      });
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = rows.map((n) => n.id ?? n.userId);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const selectedRowsPerPage = +event.target.value;
    setRowsPerPage(selectedRowsPerPage);
    setPage(0);
    if (onChangePageOrRowsPerPage)
      onChangePageOrRowsPerPage({
        pageNumber: 1,
        pageSize: selectedRowsPerPage,
      });
  };

  const visibleRows = React.useMemo(() => {
    if (!isEnableSort) {
      return rows;
    }
    
    return [...rows].sort(getComparator(order, orderBy));
  }, [order, orderBy, page, rowsPerPage, rows, isEnableSort]);

  function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key,
  ): (
    a: { [key in Key]: number | string },
    b: { [key in Key]: number | string },
  ) => number {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof any,
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  function EnhancedTableHead(props: EnhancedTableProps) {
    const {
      onSelectAllClick,
      order,
      orderBy,
      numSelected,
      rowCount,
      isEnableSelection,
      onRequestSort,
    } = props;
    const createSortHandler = (
      property: keyof any,
      event: React.MouseEvent<unknown>,
    ) => {
      onRequestSort(event, property);
    };

    return (
      <TableHead>
        <TableRow>
          {isEnableSelection && (
            <TableCell
              style={{
                width: '0%',
                height: '50%',
                backgroundColor: '#171f2f',
                border: '1px solid #2F3957FF',
                color: fontColorDarkTheme,
              }}
            >
              <Checkbox
                color="primary"
                onChange={onSelectAllClick}
                checked={rowCount > 0 && numSelected === rowCount}
              />
            </TableCell>
          )}
          {isExpandedRow && (
            <TableCell
              style={{
                width: 48,
                height: '50%',
                backgroundColor: '#171f2f',
                border: '1px solid #2F3957FF',
                color: fontColorDarkTheme,
              }}
            ></TableCell>
          )}
          {columns.map((headCell, index) => (
            <TableCell
              style={{
                width: headCell.width,
                height: '50%',
                backgroundColor: '#171f2f',
                border: '1px solid #2F3957FF',
                color: fontColorDarkTheme,
                fontWeight: 'bold',
              }}
              sx={{ color: fontColorDarkTheme }}
              key={headCell.id + index.toString()}
              align={headCell.numeric ? 'right' : 'left'}
              padding={headCell.disablePadding ? 'none' : 'normal'}
              sortDirection={orderBy === headCell.id ? order : false}
            >
              {isEnableSort ? (
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : 'asc'}
                  onClick={(event) => {
                    if (headCell.id) createSortHandler(headCell.id, event);
                  }}
                >
                  {headCell.label}
                  {orderBy === headCell.id ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              ) : (
                <>{headCell.label}</>
              )}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
    );
  }

  return (
    <>
      {onChangeMainFilter && (
        <MainFilter
          searchValue={searchValue}
          onChangeText={onChangeMainFilter}
          onSubmit={onSubmitMainFilter}
          onOpenFilter={onOpenFilter}
        />
      )}
      <Box
        sx={{
          width: '100%',
          backgroundColor: '#202940',
          margin: margin,
          borderRadius: 4,
          boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
        }}
      >
        {(title || additionalTitleComponent) && (
          <Box
            sx={{
              mb: 2,
              p: 2,
              pb: 0,
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Typography fontSize={20} component="div">{title}</Typography>
            {additionalTitleComponent}
          </Box>
        )}
        <TableContainer
          key={keyTable}
          sx={{
            color: fontColorDarkTheme,
            width: width ? width : '100%',
            height: height ? height : '100%',
            backgroundColor: '#202940',
            borderRadius: 4,
          }}
        >
          <Table
            stickyHeader
            aria-label="sticky table"
            size={size}
            key={rows.length + (totalCount ?? 0)}
          >
            <EnhancedTableHead
              isEnableSelection={isEnableSelection}
              numSelected={selected.length}
              order={order}
              onSelectAllClick={handleSelectAllClick}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
            />
            {isLoading ? (
              <TableBody>
                {[...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    {columns.map((x, idx) => (
                      <TableCell
                        key={idx}
                        style={{
                          width: 5000,
                          border: '1px solid #2F3957FF',
                          borderBottom: '1px solid #2F3957FF',
                          color: fontColorDarkTheme,
                        }}
                      >
                        <Skeleton variant="text" width="100%" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            ) : (
              visibleRows.length > 0 && (
                <TableBody style={{ border: 'none' }}>
                  {visibleRows.map((row, index) => {
                    return (
                      <React.Fragment key={row.id ?? index}>
                        <TableBodyContent
                          index={index}
                          setSelected={setSelected}
                          selected={selected}
                          row={row}
                          isEnableSelection={isEnableSelection}
                          columns={columns}
                          onRowClick={onRowClick}
                          fieldToBlockedToSelect={fieldToBlockedToSelect}
                          isExpandedRow={isExpandedRow}
                          expandedRowContent={
                            expandedRowContent && expandedRowContent(row, index)
                          }
                        />
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              )
            )}
          </Table>
          {visibleRows.length === 0 && (
            <Box sx={{ mt: 2, mb: 2, textAlign: 'center', width: '100%' }}>
              {emptyMessage ?? t('common.noItemsFound')}
            </Box>
          )}
        </TableContainer>
        {visibleRows.length > 0 && isPaginated && (
          <Box>
            <TablePagination
              rowsPerPageOptions={[10, 25, 100]}
              component="div"
              style={{ color: fontColorDarkTheme }}
              count={totalCount ?? rows.length}
              rowsPerPage={rowsPerPage}
              sx={{ display: 'flex', justifyContent: 'center' }}
              page={page}
              labelDisplayedRows={({ from, to, count }) => (
                <>
                  {from}-{to} de {count}
                </>
              )}
              labelRowsPerPage={<>Quantidade por página:</>}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Box>
        )}
      </Box>
    </>
  );
};

export default TableComponent;
